import React, { useState } from 'react';
import styles from '../../styles';
import { Input } from 'components/shared';
import * as FormikFields from 'formik-material-ui';
import { Divider, Grid, IconButton, Tooltip } from '@material-ui/core';
import { ServiceName, Values } from '../types';
import {
    getServiceName,
    renderMultipleClassNames,
    getImageByService,
} from 'helpers/helperFunctions';
import { useAppSelector } from 'index';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import i18n from 'i18n-js';
import { useFormikContext } from 'formik';
import { RestaurantDetails } from 'store/restaurantDetailsStore/reducer';
import ServiceAdditionalSettingsModal from '../ServiceAdditionalSettingsModal';
import SettingsIcon from '@material-ui/icons/Settings';
import { images } from 'assets';

type ServiceTopViewProps = {
    values: Values;
    onChangeActive: (service: ServiceName, isActive: boolean) => void;
};

export default function ServiceTopView(props: ServiceTopViewProps) {
    const restaurant = useAppSelector((state) => state.restaurantDetails.restaurantInformation);
    const classes = styles();
    const { setFieldValue } = useFormikContext<RestaurantDetails>();
    const [open, setOpen] = useState<boolean>(false);
    const [openService, setOpenService] = useState<ServiceName | null>(null);

    function renderCheckBox(name: ServiceName, disabled: boolean = false) {
        return (
            <Input
                component={FormikFields.Switch}
                checked={props.values[name].isActive}
                disabled={disabled}
                onChange={() => props.onChangeActive(name, !props.values[name].isActive)}
                name={name}
                color="primary"
                type="checkbox"
            />
        );
    }

    function renderOrderFlowSettings(serviceName: ServiceName) {
        if (
            !props.values[serviceName].isDeliverAnytime &&
            !props.values[serviceName].isPayToClose &&
            !props.values[serviceName].allowUnpaidOrder
        ) {
            return;
        }

        return !restaurant.coolBoxData.isCoolBox ? (
            <>
                <div className={classes.additionalServiceContainer}>
                    <Grid container spacing={3} justifyContent="center">
                        {props.values[serviceName].allowUnpaidOrder && (
                            <Grid item>
                                <div className={classes.serviceAdditionalSettingsImageContainer}>
                                    <img
                                        className={classes.serviceDeliveryAniTimeImage}
                                        src={images.allowUnpaidOrder}
                                        alt={i18n.t('restaurant.allowUnpaidOrder')}
                                    />
                                </div>
                            </Grid>
                        )}
                        {props.values[serviceName].isDeliverAnytime && (
                            <Grid item>
                                <div className={classes.serviceAdditionalSettingsImageContainer}>
                                    <img
                                        className={classes.serviceDeliveryAniTimeImage}
                                        src={images.deliverAnyTime}
                                        alt={i18n.t('restaurant.deliverAnytime')}
                                    />
                                </div>
                            </Grid>
                        )}
                        {props.values[serviceName].isPayToClose && (
                            <Grid item>
                                <div className={classes.serviceAdditionalSettingsImageContainer}>
                                    <img
                                        className={classes.servicePayToCloseTimeImage}
                                        src={images.payToClose}
                                        alt={i18n.t('restaurant.payToClose')}
                                    />
                                </div>
                            </Grid>
                        )}
                    </Grid>
                </div>
                <Divider className={classes.divider70px} />
            </>
        ) : null;
    }

    function handleSettingsClick(serviceName: ServiceName) {
        setOpen(true);
        setOpenService(serviceName);
    }

    function handleCloseSettings() {
        setOpen(false);
        setOpenService(null);
    }

    const renderServiceBlock = (serviceName: ServiceName, style?: any) => {
        const isOpen = open && openService === serviceName;
        return (
            <div className={renderMultipleClassNames([classes.seviceTopColumnContainer, style])}>
                <div className={classes.serviceIconContainer}>
                    {!restaurant.coolBoxData.isCoolBox && (
                        <IconButton onClick={() => handleSettingsClick(serviceName)}>
                            <SettingsIcon fontSize="inherit" />
                        </IconButton>
                    )}
                    {serviceName === 'takeOut' && (
                        <Tooltip
                            title={i18n.t('restaurant.takeOutHoursHint')}
                            arrow
                            placement="top"
                        >
                            <InfoOutlinedIcon
                                className={classes.takeoutInfoIcon}
                                color="primary"
                                fontSize="inherit"
                            />
                        </Tooltip>
                    )}
                </div>
                {getImageByService(
                    serviceName,
                    classes,
                    props.values[serviceName].isActive,
                    props.values
                )}
                <div className={classes.serviceNameContainer}>{getServiceName(serviceName)}</div>
                {renderCheckBox(
                    serviceName,
                    serviceName === 'takeOut' && !props.values.dineIn.isActive
                )}
                <Divider className={classes.divider70px} />

                {renderOrderFlowSettings(serviceName)}

                <ServiceAdditionalSettingsModal
                    open={isOpen}
                    serviceName={serviceName}
                    service={props.values[serviceName]}
                    onCancel={handleCloseSettings}
                    onSave={({ isDeliverAnytime, isPayToClose, allowUnpaidOrder }) => {
                        setFieldValue(`${serviceName}.isDeliverAnytime`, isDeliverAnytime);
                        setFieldValue(`${serviceName}.isPayToClose`, isPayToClose);
                        setFieldValue(`${serviceName}.allowUnpaidOrder`, allowUnpaidOrder);
                        handleCloseSettings();
                    }}
                />
            </div>
        );
    };

    return (
        <div className={classes.servicesTopContainer}>
            <div className={classes.serviceTopLeft}>
                MO{/* as dayName in ServiceDayRow (hiden here)*/}
            </div>
            {!restaurant.coolBoxData.isCoolBox && renderServiceBlock('homeDelivery')}
            {renderServiceBlock('takeAway')}
            {!restaurant.coolBoxData.isCoolBox && renderServiceBlock('dineIn')}
            {!restaurant.coolBoxData.isCoolBox && renderServiceBlock('takeOut')}
        </div>
    );
}
